import React, { useEffect, useState } from "react";
import { Sun, Feather, Focus as FocusIcon } from "lucide-react";

/**
 * ReadingModeToggle — the calm/comfortable/focus switch.
 *
 * Sets `document.documentElement.dataset.reading` and persists to
 * localStorage. Global CSS in index.css does the rest. Component code
 * throughout Toto does not need to change.
 */
const MODES = [
  { key: "calm",        label: "Calm",        icon: Sun,        hint: "The original — airy and elegant" },
  { key: "comfortable", label: "Comfortable", icon: Feather,    hint: "15% more contrast — ideal for most days" },
  { key: "focus",       label: "Focus",       icon: FocusIcon,  hint: "Highest readability — payroll days and long reads" },
];

const KEY = "toto.reading_mode";

export function applyStoredReadingMode() {
  // Called once at app boot so there's no flash of the wrong mode.
  try {
    const stored = localStorage.getItem(KEY) || "calm";
    document.documentElement.setAttribute("data-reading", stored);
  } catch {}
}

export default function ReadingModeToggle() {
  const [mode, setMode] = useState(() => {
    try { return localStorage.getItem(KEY) || "calm"; } catch { return "calm"; }
  });
  const [open, setOpen] = useState(false);

  useEffect(() => {
    document.documentElement.setAttribute("data-reading", mode);
    try { localStorage.setItem(KEY, mode); } catch {}
  }, [mode]);

  const Current = MODES.find((m) => m.key === mode) || MODES[0];
  const CurrentIcon = Current.icon;

  return (
    <div className="relative" data-testid="reading-mode-toggle">
      <button
        onClick={() => setOpen((o) => !o)}
        data-testid="reading-mode-button"
        aria-label={`Reading mode: ${Current.label}`}
        className="p-2 rounded-full text-stone-500 hover:text-stone-900 hover:bg-stone-100 transition-colors"
        title={`Reading mode: ${Current.label}`}
      >
        <CurrentIcon size={15} strokeWidth={1.5} />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div
            className="absolute right-0 mt-2 w-64 bg-white rounded-2xl subtle-border card-shadow z-50 overflow-hidden"
            data-testid="reading-mode-menu"
          >
            <div className="label-eyebrow px-4 pt-3 pb-1">Reading mode</div>
            <div className="px-4 pb-3 text-[11px] text-stone-500 leading-relaxed">
              The same design, tuned to how your eyes feel today.
            </div>
            <div className="border-t border-stone-100">
              {MODES.map((m) => {
                const Icon = m.icon;
                const on = m.key === mode;
                return (
                  <button
                    key={m.key}
                    onClick={() => { setMode(m.key); setOpen(false); }}
                    data-testid={`reading-mode-${m.key}`}
                    className={`w-full text-left px-4 py-3 flex items-start gap-3 hover:bg-stone-50 transition-colors ${on ? "bg-stone-50" : ""}`}
                  >
                    <div className={`shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${on ? "bg-stone-900 text-white" : "bg-stone-100 text-stone-500"}`}>
                      <Icon size={13} strokeWidth={1.5} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-stone-900 flex items-center gap-2">
                        {m.label}
                        {on && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-800 uppercase tracking-wider">On</span>}
                      </div>
                      <div className="text-[11px] text-stone-500 mt-0.5 leading-relaxed">{m.hint}</div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
